/**
 * 48点データのグラフパネル幅をユーザー操作で可変にし、 localStorage へ永続化する hook。
 *
 * 正本は proto `supply/48data.html` の `PanelResizer.attach` 設定:
 * `min: 280, max: 720, defaultWidth: 360, storageKey: '_s48ChartW', mqMin: 1100,
 *  invert: true` (= 右のグラフパネルが可変)。 キーボード 1 押下 16px は
 * 需要想定 (`useResizableTableWidth`) と同値。
 *
 * 需要想定側と同型だが、 可変なのが**左の表**か**右のグラフ**かで invert と
 * clamp 範囲が違うため hook は分けている (共有するのは `PanelResizer` 本体)。
 */
import { useCallback, useState } from 'react'

/** proto `supply/48data.html` の attach 設定と同値 */
export const CHART_MIN_WIDTH = 280
export const CHART_MAX_WIDTH = 720
export const CHART_DEFAULT_WIDTH = 360
export const CHART_RESIZE_STEP = 16
/** proto の `mqMin`。 これ未満はハンドルを出さない */
export const CHART_RESIZER_MIN_VIEWPORT = 1100

/** proto と同じキー。 localStorage は origin 単位なのでプロトと衝突しない */
const STORAGE_KEY = '_s48ChartW'

export function clampChartWidth(width: number): number {
  return Math.min(CHART_MAX_WIDTH, Math.max(CHART_MIN_WIDTH, width))
}

/**
 * 保存済みの幅を読む。 localStorage が使えない環境 (Safari private 等) や
 * 壊れた値では既定幅にフォールバックする。
 */
function readStoredWidth(): number {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === null) return CHART_DEFAULT_WIDTH
    const parsed = Number.parseInt(saved, 10)
    if (Number.isNaN(parsed)) return CHART_DEFAULT_WIDTH
    return clampChartWidth(parsed)
  } catch {
    return CHART_DEFAULT_WIDTH
  }
}

type ResizableChartWidth = {
  width: number
  /** persist=false はドラッグ中の逐次更新 (保存しない) */
  changeWidth: (width: number, persist: boolean) => void
  /** 保存幅を破棄して既定幅へ戻す (ハンドルのダブルクリック) */
  resetWidth: () => void
}

export function useResizableChartWidth(): ResizableChartWidth {
  const [width, setWidth] = useState(readStoredWidth)

  const changeWidth = useCallback((next: number, persist: boolean) => {
    const clamped = clampChartWidth(next)
    setWidth(clamped)
    if (!persist) return
    try {
      window.localStorage.setItem(STORAGE_KEY, String(clamped))
    } catch {
      // localStorage が使えない環境では幅の永続化だけ諦める (操作は効く)
    }
  }, [])

  const resetWidth = useCallback(() => {
    setWidth(CHART_DEFAULT_WIDTH)
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // 同上
    }
  }, [])

  return { width, changeWidth, resetWidth }
}
